import * as React from "react";
import { cn } from "@/lib/utils";

export interface ShineEffectProps extends React.HTMLAttributes<HTMLDivElement> {
  enabled?: boolean;
  interval?: number;
  duration?: number;
  opacity?: number;
  width?: number;
  angle?: number;
  children: React.ReactNode;
  asChild?: boolean;
}

const ShineEffect = React.forwardRef<HTMLDivElement, ShineEffectProps>(
  ({
    className,
    enabled = true,
    interval = 100,
    duration = 2000,
    opacity = 20,
    width = 10,
    angle = 45,
    children,
    ...props
  }, ref) => {
    const [animating, setAnimating] = React.useState(false);
    const animationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
    
    // Animation cycle for shine effect
    const startShineAnimation = React.useCallback(() => {
      setAnimating(true);
      
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      
      animationTimeoutRef.current = setTimeout(() => {
        setAnimating(false);
        
        if (enabled) {
          intervalRef.current = setTimeout(() => {
            startShineAnimation();
          }, interval);
        }
      }, duration);
    }, [interval, enabled, duration]);
    
    // Start the shine animation cycle
    React.useEffect(() => {
      if (enabled) {
        startShineAnimation();
      } else {
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
          intervalRef.current = null;
        }
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        setAnimating(false);
      }
      
      return () => {
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
          intervalRef.current = null;
        }
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
      };
    }, [enabled, startShineAnimation]);

    return (
      <div 
        ref={ref}
        className={cn("relative overflow-hidden", className)}
        {...props}
      >
        {children}
        
        {/* Shine effect */}
        {enabled && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div
              style={{
                position: 'absolute',
                top: '-100%',
                left: '-100%',
                width: '300%',
                height: '300%',
                background: `linear-gradient(${angle}deg, 
                  transparent, 
                  transparent 35%, 
                  rgba(255, 255, 255, ${opacity/100}) ${45 - width/2}%, 
                  rgba(255, 255, 255, ${opacity/100}) ${45 + width/2}%, 
                  transparent ${45 + width + 10}%, 
                  transparent)`,
                transform: `translateX(${animating ? '50%' : '-50%'})`,
                transition: animating ? `transform ${duration}ms cubic-bezier(0.4, 0.0, 0.2, 1)` : 'none',
              }}
            />
          </div>
        )}
      </div>
    );
  }
);

ShineEffect.displayName = "ShineEffect";

export { ShineEffect };